import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/global.css';
import '../styles/pages/Landing.css';
import API from '../api/API';
import CustomToolbar from './components/CustomToolbar';
import * as messages from '../utils/messages';
import { Toast } from 'primereact/toast';
import * as mapas from '../utils/mapas'
import { Dock } from 'primereact/dock';
import { MenuItem } from 'primereact/menuitem';
import { ReactComponent as BackIcon } from '../images/back.svg';
import { ReactComponent as ListIcon } from '../images/book-library-svgrepo-com.svg';
import DialogSearch from './dialogs/Search';
import CadastroPessoa from './dialogs/CadastroPessoa';
import { newPessoa, PessoaType, PlanoType, ImovelType } from '../db/Orm';
import Auth from '../auth/Authenticator';

function Municipio() {
    const auth = Auth.useAuth();
    var [municipio] = useLocation().state

    const mapRef = useRef<any>(null);
    const [mapa, setMapa] = useState<L.Map | null>(null)
    const [propriedades, setPropriedades] = useState<Array<ImovelType>>([])
    const [pessoas, setPessoas] = useState<Array<PessoaType>>([])
    const [planos, setPlanos] = useState<PlanoType[]>([])
    const navigator = useNavigate()
    const [cars, setCars] = useState<Array<L.GeoJSON>>([])
    const loadCars = async (map: L.Map, cars: Array<L.GeoJSON>, setCars: Function) => {
        if (cars.length === 0) {
            const cs = await mapas.drawCarsProcessingPopups(map, municipio.id, global.uf)
            setCars(cs)
        }
        else
            cars.map((car: L.GeoJSON) => car.remove())
    }

    // Definições de tipo para clareza
    interface ProcessGeojsonParams {
        startDate: string; // Formato "YYYY-MM-DD"
        endDate: string;   // Formato "YYYY-MM-DD"
        geojsonBody: any;  // Seu objeto GeoJSON
        timeoutMs?: number; // Timeout em milissegundos (opcional)
    }

    interface ProcessGeojsonResult {
        success: boolean;
        data?: Blob;       // O blob do PDF se sucesso
        fileName?: string;   // Nome do arquivo do PDF, extraído do Content-Disposition
        error?: string;      // Mensagem de erro se falha
        statusCode?: number; // Código de status HTTP da resposta
    }

    /**
     * Extrai o nome do arquivo do header Content-Disposition.
     * Lida com formatos como:
     * filename="nome.pdf"
     * filename*=UTF-8''nome%20com%20espa%C3%A7os.pdf
     */
    function getFileNameFromContentDisposition(contentDisposition: string | null): string {
        let fileName = 'relatorio_processado.pdf'; // Nome padrão
        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']+)['"]?/i);
            if (fileNameMatch && fileNameMatch[1]) {
                try {
                    fileName = decodeURIComponent(fileNameMatch[1]);
                } catch (e) {
                    // Fallback se decodeURIComponent falhar
                    fileName = fileNameMatch[1];
                }
            }
        }
        return fileName;
    }

    /**
     * Função para requisitar o processamento do GeoJSON ao backend.
     * @param params Parâmetros da requisição, incluindo datas, GeoJSON e timeout opcional.
     * @returns Uma promessa que resolve para um objeto ProcessGeojsonResult.
     */
    async function requestProcessGeojson(params: ProcessGeojsonParams): Promise<ProcessGeojsonResult> {
        const { startDate, endDate, geojsonBody, timeoutMs = 300000 } = params; // Timeout padrão de 5 minutos (300000 ms)

        const baseUrl = "https://api.monitorabahia.gearspark.com.br/api/process-geojson/";
        const url = new URL(baseUrl);
        url.searchParams.append('start', startDate);
        url.searchParams.append('end', endDate);

        const controller = new AbortController();
        const signal = controller.signal;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (timeoutMs > 0) {
            timeoutId = setTimeout(() => {
                console.warn(`Requisição para ${url.toString()} abortada por timeout de ${timeoutMs}ms.`);
                controller.abort();
            }, timeoutMs);
        }

        try {
            console.log(`Enviando POST para: ${url.toString()}`);
            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Adicione aqui outros cabeçalhos necessários, como Authorization, se houver.
                },
                body: JSON.stringify(geojsonBody), // Envie o GeoJSON diretamente como corpo
                signal, // Permite o abort da requisição
            });

            if (timeoutId) {
                clearTimeout(timeoutId); // Limpa o timeout se a requisição completar ou falhar antes
            }

            const contentType = response.headers.get('Content-Type');
            const contentDisposition = response.headers.get('Content-Disposition');

            if (response.ok) {
                if (contentType && contentType.includes('application/pdf')) {
                    const blob = await response.blob();
                    const fileName = getFileNameFromContentDisposition(contentDisposition);
                    console.log(`PDF recebido: ${fileName}, tamanho: ${blob.size} bytes`);
                    return {
                        success: true,
                        data: blob,
                        fileName: fileName,
                        statusCode: response.status,
                    };
                } else {
                    // Resposta OK, mas não é PDF - pode ser um JSON inesperado de sucesso
                    const responseText = await response.text();
                    console.warn("Resposta OK, mas não é PDF:", responseText);
                    return {
                        success: false, // Considerar como falha se um PDF era esperado
                        error: `Resposta inesperada do servidor (esperava PDF): ${responseText.substring(0, 100)}...`,
                        statusCode: response.status,
                    };
                }
            } else {
                // Tratar erros HTTP (4xx, 5xx)
                let errorMessage = `Erro ${response.status}: ${response.statusText}`;
                if (contentType && contentType.includes('application/json')) {
                    try {
                        const errorJson = await response.json();
                        errorMessage = errorJson.detail || JSON.stringify(errorJson);
                    } catch (e) {
                        // Se não conseguir parsear o JSON de erro, usa o texto da resposta
                        errorMessage = await response.text();
                    }
                } else {
                    errorMessage = await response.text();
                }
                console.error(`Erro na requisição: Status ${response.status}, Mensagem: ${errorMessage}`);
                return {
                    success: false,
                    error: errorMessage,
                    statusCode: response.status,
                };
            }
        } catch (error: any) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (error.name === 'AbortError') {
                console.error('A requisição foi abortada devido ao timeout.');
                return { success: false, error: `A requisição excedeu o tempo limite de ${timeoutMs / 1000} segundos.`, statusCode: 408 };
            }
            console.error('Erro na requisição fetch:', error);
            return { success: false, error: `Erro de rede ou na requisição: ${error.message}` };
        }
    }


    useEffect(() => {
        if (!mapa) {
            const map = mapas.createMapOnBahia(mapRef)
            mapas.addButton(map, () => loadCars(map, cars, setCars), 'topleft', 'https://car.gov.br/public/img/logo_g.png')
            // Função de callback para o botão de processamento
            // const handleProcessAndDownload = async () => { // 👈 Tornar async
            //     console.log("Botão de processar GeoJSON clicado.");
            //     // Adicione aqui um feedback visual de que o processamento começou (ex: um spinner)
            //     // alert("Processando GeoJSON... Isso pode levar alguns instantes.");

            //     const geojsonParaProcessar = {"type":"Feature","id":"sicar_imoveis_ba.14238844","geometry":{"type":"MultiPolygon","coordinates":[[[[-41.62545286,-13.26841058],[-41.62357209,-13.26765171],[-41.6231151,-13.27040621],[-41.62157536,-13.27068587],[-41.62142416,-13.27084123],[-41.62131538,-13.2710426],[-41.62126741,-13.27140286],[-41.6211521,-13.27178467],[-41.62104741,-13.272036],[-41.62090206,-13.27223601],[-41.62072077,-13.27244426],[-41.62055862,-13.27262365],[-41.62039075,-13.27278725],[-41.62020166,-13.27309162],[-41.6203175,-13.27317858],[-41.62043851,-13.2731898],[-41.62062407,-13.27326523],[-41.6207357,-13.27320977],[-41.62088758,-13.27317446],[-41.62098545,-13.27317714],[-41.62113214,-13.27317324],[-41.62128289,-13.27316545],[-41.62143851,-13.27312967],[-41.62173545,-13.27307701],[-41.62182776,-13.27304264],[-41.62193865,-13.27298762],[-41.62204458,-13.2729528],[-41.62214876,-13.27294304],[-41.62220738,-13.27295313],[-41.62229877,-13.27302682],[-41.62245554,-13.27328852],[-41.62253032,-13.27339825],[-41.6225414,-13.27350336],[-41.62258454,-13.27358497],[-41.62283733,-13.27374445],[-41.62300752,-13.27386317],[-41.62320895,-13.27407095],[-41.6233296,-13.27418487],[-41.62345252,-13.27423686],[-41.62364945,-13.27429762],[-41.62383641,-13.27426968],[-41.62409284,-13.27431044],[-41.62421573,-13.27401132],[-41.62422807,-13.27352024],[-41.62408367,-13.27262791],[-41.62412307,-13.27252002],[-41.6241435,-13.27228581],[-41.62409907,-13.27199023],[-41.62401576,-13.27162324],[-41.62405895,-13.27095113],[-41.62483484,-13.2709297],[-41.62519208,-13.27080085],[-41.62527086,-13.27000482],[-41.6252031,-13.26935552],[-41.62545286,-13.26841058]]]]},"geometry_name":"geo_area_imovel","properties":{"cod_imovel":"BA-2900108-9EEB920498B34D3C864767D2601E8FB8","status_imovel":"AT","dat_criacao":"2025-06-04T16:41:00.246Z","data_atualizacao":null,"area":17.99,"condicao":"Aguardando análise","uf":"BA","municipio":"Abaíra","cod_municipio_ibge":2900108,"m_fiscal":0.27,"tipo_imovel":"IRU"}}

            //     try {
            //         const result = await requestProcessGeojson({ // 👈 Usar await para esperar o resultado
            //             endDate: '2025-02-28',
            //             startDate: '2025-02-01',
            //             timeoutMs: 300000, // 300.000ms = 5 minutos. 3.000.000ms = 50 minutos (verifique se este valor está correto)
            //             geojsonBody: geojsonParaProcessar
            //         });

            //         if (result.success && result.data && result.fileName) {
            //             console.log(`Preparando download para: ${result.fileName}`);
            //             const downloadUrl = window.URL.createObjectURL(result.data);
            //             const link = document.createElement('a');
            //             link.href = downloadUrl;
            //             link.setAttribute('download', result.fileName); // Define o nome do arquivo
            //             document.body.appendChild(link);
            //             link.click(); // Simula o clique no link para iniciar o download
            //             document.body.removeChild(link); // Remove o link do DOM
            //             window.URL.revokeObjectURL(downloadUrl); // Libera a memória do objeto URL

            //             // Adicione aqui um feedback visual de sucesso
            //             // alert(`Download de "${result.fileName}" iniciado!`);

            //         } else {
            //             console.error("Falha ao processar ou obter PDF:", result.error, "Status:", result.statusCode);
            //             // Adicione aqui um feedback visual de erro
            //             alert(`Falha no processamento: ${result.error || 'Erro desconhecido'} (Status: ${result.statusCode || 'N/A'})`);
            //         }
            //     } catch (e) {
            //         console.error("Erro inesperado ao tentar processar e baixar:", e);
            //         alert("Ocorreu um erro inesperado. Verifique o console.");
            //     } finally {
            //         // Remova o feedback visual de carregamento aqui
            //     }
            // };

            // mapas.addButton(map, handleProcessAndDownload, 'topleft', 'https://www.svgrepo.com/download/434422/process.svg');
            setMapa(map)
        }
    }, []);

    const carregarPropriedades = async () => {
        console.log('Carregando propriedades aprovadas')
        // if (auth.user?.groups[0].name === 'Fiscal ADAB') {
        //     const ps = await API.getPropriedades(navigator, () => messages.errorMessage(toast.current), undefined, undefined, STATUS.APROVADO)
        //     console.log('PS', ps)
        //     try {
        //         ps.map((p: any) => p.municipio = global.municipios.filter((m: any) => m.id === p.municipio)[0])
        //         setPropriedades(ps)
        //     } catch (e) { console.log('Error carregar propriedades novas', e) }
        // } else {
        //     console.log('Usuario nao é fiscal', auth.user?.pessoas_user[0])
        //     const ps = await API.getPropriedades(navigator, () => messages.errorMessage(toast.current), undefined, auth.user?.pessoas_user[0])
        //     console.log('PS', ps)
        //     try {
        //         ps.map((p: any) => p.municipio = global.municipios.filter((m: any) => m.id === p.municipio)[0])
        //         setPropriedades(ps)
        //     } catch (e) { console.log('Error carregar propriedades novas', e) }
        // }
        setPropriedades(await API.getPropriedades(navigator, () => messages.errorMessage(toast.current), municipio))
        setLoading(false)
    }

    const carregarPessoas = async () => {
        console.log('Carregando pessoas aprovadas')
        if (auth.hasPermission('monitorabahia.pessoas_view_all')) {
            const ps: Array<PessoaType> = []
            try {
                propriedades.map((p: ImovelType) => {
                    if (p.proprietario?.pessoas_user) {
                        const proprietario = p.proprietario.pessoas_user[0]
                        if (ps.findIndex((v) => v.id === proprietario.id) < 0)
                            ps.push(proprietario)
                    }
                })
                setPessoas(ps)
            } catch (e) { console.log('Error carregar propriedades novas', e) }
        } else {
            console.log('Usuario nao é fiscal', auth.user?.pessoa)
            const ps: Array<PessoaType> = []
            try {
                propriedades.map((p: ImovelType) => {
                    if (p.proprietario?.pessoas_user) {
                        const proprietario = p.proprietario.pessoas_user[0]
                        if (ps.findIndex((v) => v.id === proprietario.id) < 0)
                            ps.push(proprietario)
                    }
                })
                setPessoas(ps)
            } catch (e) { console.log('Error carregar propriedades novas', e) }
        }
    }
    useEffect(() => {
        console.log('FAZER ACONTECER')
        setPlanos(
            municipio.planos?.filter((pl: PlanoType) => pl.propriedade.municipio === municipio.id))
    }, [])

    useEffect(() => {
        if (!mapa) return
        carregarPropriedades();
        mapas.loadMapaMunicipio(mapa, municipio, mapas.estiloContorno)
    }, [mapa])

    useEffect(() => {
        if (propriedades?.length === 0) return
        console.log('Propriedades:', propriedades)
        if (propriedades) {
            mapas.loadPropriedades(mapa, propriedades, false)
            carregarPessoas()
        }

    }, [propriedades])

    const [loading, setLoading] = useState(true)
    const toast = useRef<Toast | null>(null)

    const [showDialog, setShowDialog] = useState(false)

    const InfoMenu = {
        label: 'Pesquisar', icon: () => <ListIcon />
        , command: () => { setShowDialog(true) }
    }
    const VoltarMenu = {
        label: 'Sair', icon: () => <BackIcon />,
        command: () => {
            navigator('/home')
        }
    }
    const items: MenuItem[] =
        !auth.user ? [VoltarMenu] :
            auth.user?.groups[0].name === 'Fiscal ADAB' ? [InfoMenu, VoltarMenu] :
                auth.user?.groups[0].name === 'Produtor' ? [InfoMenu, VoltarMenu] :
                    auth.user?.groups[0].name === 'Supervisor PROALBA' ? [InfoMenu, VoltarMenu] :
                        auth.user?.groups[0].name === 'Supervisor AIBA' ? [InfoMenu, VoltarMenu] :
                            auth.user?.groups[0].name === 'Desenvolvedor' ? [InfoMenu, VoltarMenu] :
                                [InfoMenu, VoltarMenu];
    const openPropriedade = (propriedade: any) => (navigator('/propriedade', { state: [propriedade, municipio] }))
    const [pessoa, setPessoa] = useState<PessoaType>(newPessoa())
    const [showDialogCadastroPessoa, setShowDialogCadastroPessoa] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const openPessoa = (pessoa: PessoaType, edit: boolean) => {
        console.log('Open pessoa:', pessoa)
        setPessoa(pessoa)
        setActiveIndex(0)
        setShowDialogCadastroPessoa(true)
    }
    return (
        <div className='landing'>
            <Toast ref={toast} position='center' />
            <CustomToolbar titulo={'MUNICÍPIO ' + municipio.nome.toUpperCase()}
                loading={loading} user={auth.user}
            />

            <DialogSearch municipios={[municipio]} pessoas={pessoas} propriedades={propriedades} cultivos={[]} openMunicipio={() => { }} openPessoa={openPessoa} openPropriedade={openPropriedade} openCultivo={() => { }} visible={showDialog} setVisible={setShowDialog} planos={planos} />
            <CadastroPessoa function={setShowDialogCadastroPessoa} state={showDialogCadastroPessoa} pessoas={pessoas} pessoa={pessoa} setPessoa={setPessoa} activeIndex={activeIndex} setActiveIndex={setActiveIndex} edit={false} />

            <div id='map' ref={mapRef} />
            <div className="dock-window" style={{ zIndex: 1 }}>
                <Dock model={items} position={'bottom'} />
            </div>
        </div>
    );
}

export default Municipio;
